/**
 * DIAS Command Centre — single consolidated control surface for the
 * Compliance page. Three tabs:
 *
 *   1. Scorecard           — overall ring + 3-bucket bars + action buttons
 *                             (Master Checklist link, Evidence Pack ZIP,
 *                             QIP Submit, QIP Share, Friday Digest)
 *   2. Master Checklist    — inline DIAS validator (5 modules · ~30 checks)
 *                             with one-click attestations and module filters
 *   3. Evidence audit log  — every evidence_uploads record with filename,
 *                             parent reference, uploader, timestamp +
 *                             quick filters (parent type / since)
 *
 * Replaces the previous standalone DIASReadinessWidget on /compliance.
 * The dashboard home page keeps the slim widget for at-a-glance visibility.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Shield, ShieldCheck, ShieldAlert, CheckCircle2, AlertTriangle, ArrowRight, Sparkles, Mail, Loader2,
  Download, Share2, Copy, Trash2, ListChecks, Send, XCircle, Circle, RefreshCw,
  Printer, FileText, History, FileSearch,
} from 'lucide-react';

import QIPSubmitModal from './QIPSubmitModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUCKET_LINKS = {
  'Equipment Register':   { href: '/equipment',            label: 'Open equipment' },
  'Staff Licensing':      { href: '/people',               label: 'Manage staff' },
  'Radiation Governance': { href: '/radiation-governance', label: 'Open governance' },
};

const STATUS_META = {
  pass:        { color: '#16A34A', icon: CheckCircle2,  label: 'Pass' },
  fail:        { color: '#DC2626', icon: XCircle,        label: 'Fail' },
  warning:     { color: '#D97706', icon: AlertTriangle,  label: 'Review' },
  manual_only: { color: '#6B7280', icon: Circle,         label: 'Attest' },
};

// Friendly labels for each parent_type used by EvidenceUploads
const PARENT_TYPE_LABELS = {
  user_licence:        'Staff licence',
  practice_cert:       'Practice cert (RSP/RML)',
  equipment_service:   'Equipment service log',
  rso:                 'RSO appointment',
  epa:                 'EPA licence',
  lead_apron:          'Lead apron test',
  tld_reading:         'TLD reading',
};

function color(score) {
  if (score >= 90) return '#16A34A';
  if (score >= 70) return '#0EA5E9';
  if (score >= 50) return '#D97706';
  return '#DC2626';
}

const TABS = [
  { id: 'scorecard',  label: 'Scorecard',         Icon: Shield },
  { id: 'checklist',  label: 'Master checklist',  Icon: ListChecks },
  { id: 'evidence',   label: 'Evidence audit log', Icon: FileSearch },
];

export default function DIASCommandCentre() {
  const [tab, setTab] = useState('scorecard');
  const [readiness, setReadiness] = useState(null);
  const [readinessErr, setReadinessErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/dias-readiness`, { withCredentials: true })
      .then(r => setReadiness(r.data))
      .catch(e => setReadinessErr(e.response?.status === 403 ? 'forbidden' : 'error'));
  }, []);

  if (readinessErr === 'forbidden' || readinessErr === 'error') return null;

  const overallColor = readiness ? color(readiness.overall_score) : '#0EA5E9';

  return (
    <div className="bg-card border border-border rounded-2xl mb-8 overflow-hidden" data-testid="dias-command-centre">
      {/* Header strip */}
      <div className="px-5 pt-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
              style={{ backgroundColor: `${overallColor}15` }}>
              <Shield className="w-4 h-4" style={{ color: overallColor }} />
            </div>
            <div className="min-w-0">
              <h2 className="font-['Outfit'] text-base sm:text-lg font-semibold text-foreground flex items-center gap-2 flex-wrap">
                DIAS command centre
                {readiness && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: `${overallColor}15`, color: overallColor }}>
                    {readiness.overall_score}% audit-ready
                  </span>
                )}
              </h2>
              <p className="text-[11px] text-muted-foreground">
                Scorecard · master checklist · evidence audit log — single command surface for QIP readiness.
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-border" data-testid="dias-cc-tabs">
          {TABS.map(t => {
            const Icon = t.Icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`dias-cc-tab-${t.id}`}
                className={`-mb-px inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition ${
                  active ? '' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                style={active ? { borderColor: overallColor, color: overallColor } : {}}>
                <Icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab body */}
      <div className="p-5">
        {tab === 'scorecard' && <ScorecardTab readiness={readiness} overallColor={overallColor} />}
        {tab === 'checklist' && <MasterChecklistTab overallColor={overallColor} />}
        {tab === 'evidence'  && <EvidenceAuditTab  overallColor={overallColor} />}
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────────── */
/* Tab 1 — Scorecard                                                         */
/* ────────────────────────────────────────────────────────────────────────── */
function ScorecardTab({ readiness, overallColor }) {
  const [sending, setSending] = useState(false);
  const [sentMsg, setSentMsg] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);

  const sendDigest = async () => {
    setSending(true); setSentMsg(null);
    try {
      await axios.post(`${API}/dias-readiness/send-digest`, {}, { withCredentials: true });
      setSentMsg({ type: 'success', text: 'Friday digest sent to all admin/PM recipients.' });
    } catch (e) {
      setSentMsg({ type: 'error', text: e.response?.data?.detail || 'Send failed.' });
    }
    setSending(false);
    setTimeout(() => setSentMsg(null), 6000);
  };

  const downloadPack = async () => {
    try {
      const r = await axios.get(`${API}/dias-evidence-pack`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `DIAS_evidence_pack_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) {
      setSentMsg({ type: 'error', text: e.response?.data?.detail || 'Download failed.' });
    }
  };

  if (!readiness) {
    return (
      <div className="animate-pulse" data-testid="dias-cc-scorecard-loading">
        <div className="h-32 bg-muted/40 rounded-xl mb-3" />
        <div className="space-y-2">
          <div className="h-8 bg-muted/40 rounded" />
          <div className="h-8 bg-muted/40 rounded" />
          <div className="h-8 bg-muted/40 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dias-cc-scorecard">
      {/* Action buttons */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:flex lg:flex-wrap lg:items-center gap-2 mb-4">
        <button onClick={downloadPack} data-testid="dias-cc-evidence-pack-btn"
          title="Download a ZIP bundle with the cover scorecard + every register (equipment, QA, staff licensing, RSO, EPA, TLD, lead aprons, SOPs) — ready to upload to QIP."
          className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
          style={{ borderColor: `${overallColor}40`, color: overallColor }}>
          <Download className="w-3 h-3" /> Evidence pack (ZIP)
        </button>
        <button onClick={() => setSubmitOpen(true)} data-testid="dias-cc-submit-qip-btn"
          title="Email the evidence pack ZIP + scorecard PDF + master-checklist CSV directly to a QIP intake address."
          className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md text-white"
          style={{ backgroundColor: overallColor }}>
          <Send className="w-3 h-3" /> Submit to QIP
        </button>
        <button onClick={() => setShareOpen(true)} data-testid="dias-cc-share-link-btn"
          title="Generate a tokenised read-only URL to email QIP before site visit."
          className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
          style={{ borderColor: `${overallColor}40`, color: overallColor }}>
          <Share2 className="w-3 h-3" /> QIP share link
        </button>
        <button onClick={sendDigest} disabled={sending} data-testid="dias-cc-send-digest-btn"
          title="Email this scorecard + licence-expiry list + overdue equipment to every admin/PM. Cron auto-sends every Friday 21:00 UTC."
          className="inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border disabled:opacity-50"
          style={{ borderColor: `${overallColor}40`, color: overallColor }}>
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
          {sending ? 'Sending…' : 'Send Friday digest now'}
        </button>
        <span className="ml-auto text-[10px] text-muted-foreground">As of {readiness.as_of}</span>
      </div>

      {sentMsg && (
        <div data-testid="dias-cc-digest-msg"
          className={`rounded-lg border p-2 text-xs mb-3 ${sentMsg.type === 'success'
            ? 'bg-green-500/5 border-green-500/30 text-green-600'
            : 'bg-red-500/5 border-red-500/30 text-red-600'}`}>
          {sentMsg.text}
        </div>
      )}

      {(readiness.practice?.lspn || readiness.practice?.hpi_o) && (
        <div className="flex items-center gap-3 flex-wrap text-[11px] mb-4 pb-3 border-b border-border" data-testid="dias-cc-practice-ids">
          {readiness.practice.name && <span className="font-semibold text-foreground">{readiness.practice.name}</span>}
          {readiness.practice.lspn && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono"
              style={{ backgroundColor: `${overallColor}10`, color: overallColor }}>
              <span className="font-bold tracking-wider">LSPN</span> {readiness.practice.lspn}
            </span>
          )}
          {readiness.practice.hpi_o && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono"
              style={{ backgroundColor: `${overallColor}10`, color: overallColor }}>
              <span className="font-bold tracking-wider">HPI-O</span> {readiness.practice.hpi_o}
            </span>
          )}
          {readiness.practice.dias_accreditation_no && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-mono"
              style={{ backgroundColor: `${overallColor}10`, color: overallColor }}>
              <span className="font-bold tracking-wider">DIAS</span> {readiness.practice.dias_accreditation_no}
            </span>
          )}
          <Link to="/practice-settings" data-testid="dias-cc-edit-practice-link"
            className="ml-auto text-[10px] text-muted-foreground hover:text-foreground hover:underline">
            edit →
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="flex flex-col items-center justify-center p-3 rounded-xl border border-border bg-background"
          data-testid="dias-cc-overall-score">
          <ScoreRing value={readiness.overall_score} color={overallColor} />
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mt-2">QIP entry-level</p>
          <p className="text-xs font-semibold text-foreground">
            {readiness.buckets.reduce((a, b) => a + b.passed, 0)} of {readiness.buckets.reduce((a, b) => a + b.total, 0)} checks pass
          </p>
        </div>
        <div className="md:col-span-3 grid grid-cols-1 gap-2">
          {readiness.buckets.map(b => <BucketRow key={b.label} bucket={b} />)}
        </div>
      </div>

      {shareOpen && <ShareLinkModal onClose={() => setShareOpen(false)} />}
      {submitOpen && <QIPSubmitModal onClose={() => setSubmitOpen(false)} />}
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────────── */
/* Tab 2 — Master Checklist (inline, ~30 checks across 5 modules)            */
/* ────────────────────────────────────────────────────────────────────────── */
function MasterChecklistTab({ overallColor }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState('open'); // open | all | manual
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/dias-validator`, { withCredentials: true });
      setReport(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load validator.');
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const attest = async (check_id, ticked, note = '') => {
    setBusyId(check_id);
    try {
      await axios.post(`${API}/dias-validator/attest`, { check_id, ticked, note }, { withCredentials: true });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Attestation failed.');
    }
    setBusyId(null);
  };

  const filtered = useMemo(() => {
    if (!report) return [];
    return report.modules.map(m => ({
      ...m,
      checks: m.checks.filter(c => {
        if (filter === 'open')   return c.status === 'fail' || c.status === 'warning' || c.status === 'manual_only';
        if (filter === 'manual') return c.manual;
        return true;
      }),
    })).filter(m => m.checks.length > 0);
  }, [report, filter]);

  const downloadCsv = () => {
    if (!report) return;
    const rows = [['Module', 'Check', 'Status', 'Detail', 'Suggestion']];
    report.modules.forEach(m => m.checks.forEach(c => {
      rows.push([m.label, c.label, c.status, (c.detail || '').replace(/\n/g, ' '), (c.suggestion || '').replace(/\n/g, ' ')]);
    }));
    const csv = rows.map(r => r.map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `DIAS_master_checklist_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  };

  if (loading && !report) {
    return (
      <div className="animate-pulse space-y-3" data-testid="dias-cc-checklist-loading">
        <div className="h-5 w-1/3 bg-muted rounded" />
        <div className="h-3 w-2/3 bg-muted/60 rounded" />
        <div className="h-3 w-1/2 bg-muted/60 rounded" />
      </div>
    );
  }
  if (err) {
    return <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-600" data-testid="dias-cc-checklist-error">{err}</div>;
  }
  if (!report) return null;

  return (
    <div data-testid="dias-cc-checklist">
      <ChecklistSummary report={report} />

      <div className="flex items-center gap-2 my-4 flex-wrap" data-testid="dias-cc-filters">
        {[
          { id: 'open',   label: `Needs action (${report.counts.fail + report.counts.warning + report.counts.manual_only})` },
          { id: 'all',    label: `All (${report.total_checks})` },
          { id: 'manual', label: 'Manual attestations only' },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            data-testid={`dias-cc-filter-${f.id}`}
            className={`text-[11px] font-semibold px-3 py-1.5 rounded-full border transition ${
              filter === f.id ? 'text-white' : 'text-muted-foreground hover:text-foreground border-border'
            }`}
            style={filter === f.id ? { backgroundColor: overallColor, borderColor: overallColor } : {}}>
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={load} disabled={loading} data-testid="dias-cc-checklist-refresh"
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border disabled:opacity-50"
            style={{ borderColor: `${overallColor}40`, color: overallColor }}>
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
          </button>
          <button onClick={downloadCsv} data-testid="dias-cc-checklist-csv"
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
            style={{ borderColor: `${overallColor}40`, color: overallColor }}>
            <Download className="w-3 h-3" /> CSV
          </button>
          <Link to="/dias-checklist" data-testid="dias-cc-checklist-fullscreen"
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
            style={{ borderColor: `${overallColor}40`, color: overallColor }}>
            <Printer className="w-3 h-3" /> Print view
          </Link>
        </div>
      </div>

      {filtered.map(m => (
        <ChecklistModule key={m.label} module={m} attest={attest} busyId={busyId} overallColor={overallColor} />
      ))}

      {filtered.length === 0 && (
        <div className="rounded-2xl border border-border p-6 text-center" data-testid="dias-cc-checklist-empty">
          <CheckCircle2 className="w-7 h-7 mx-auto text-green-600 mb-2" />
          <p className="text-sm font-semibold text-foreground">Nothing matches the current filter.</p>
          <p className="text-xs text-muted-foreground">Switch to "All" to see every check.</p>
        </div>
      )}
    </div>
  );
}

function ChecklistSummary({ report }) {
  const buckets = [
    { id: 'pass',        ...STATUS_META.pass },
    { id: 'fail',        ...STATUS_META.fail },
    { id: 'warning',     ...STATUS_META.warning },
    { id: 'manual_only', ...STATUS_META.manual_only },
  ];
  const c = report.pass_pct >= 90 ? '#16A34A' : report.pass_pct >= 70 ? '#0EA5E9' : report.pass_pct >= 50 ? '#D97706' : '#DC2626';
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-center" data-testid="dias-cc-checklist-summary">
      <div className="text-center md:border-r md:border-border md:pr-3">
        <div className="text-3xl font-bold tabular-nums" style={{ color: c }}>{report.pass_pct}%</div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Audit-ready</div>
        <div className="text-[10px] text-muted-foreground mt-0.5">{report.total_checks} total checks</div>
      </div>
      {buckets.map(b => {
        const Icon = b.icon;
        const n = report.counts[b.id] || 0;
        return (
          <div key={b.id} data-testid={`dias-cc-count-${b.id}`}
            className="flex items-center gap-2 px-2 py-1.5 rounded-lg"
            style={{ backgroundColor: `${b.color}10`, border: `1px solid ${b.color}30` }}>
            <Icon className="w-4 h-4 shrink-0" style={{ color: b.color }} />
            <div className="min-w-0">
              <div className="text-base font-bold tabular-nums" style={{ color: b.color }}>{n}</div>
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{b.label}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ChecklistModule({ module, attest, busyId, overallColor }) {
  return (
    <div className="border border-border rounded-2xl p-4 mt-3" data-testid={`dias-cc-module-${module.label[0]}`}>
      <h3 className="font-['Outfit'] text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
        <Shield className="w-3.5 h-3.5" style={{ color: overallColor }} />
        {module.label}
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground ml-auto">
          {module.checks.filter(c => c.status === 'pass').length}/{module.checks.length} pass
        </span>
      </h3>
      <ul className="divide-y divide-border">
        {module.checks.map((c, idx) => (
          <ChecklistRow key={`${c.label}-${idx}`} check={c} attest={attest} busyId={busyId} />
        ))}
      </ul>
    </div>
  );
}

function ChecklistRow({ check, attest, busyId }) {
  const meta = STATUS_META[check.status] || STATUS_META.manual_only;
  const Icon = meta.icon;
  const ticked = check.status === 'pass';
  const busy = busyId === check.check_id;
  return (
    <li className="py-2.5 flex items-start gap-3" data-testid={`dias-cc-check-${(check.check_id || check.label).slice(0, 30)}`}>
      {check.manual ? (
        <button onClick={() => attest(check.check_id, !ticked)} disabled={busy}
          data-testid={`dias-cc-attest-${check.check_id}`}
          className="mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 disabled:opacity-50"
          style={{ borderColor: meta.color, backgroundColor: ticked ? meta.color : 'transparent' }}
          title={ticked ? 'Click to un-attest' : 'Click to attest'}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin text-white" />
            : ticked ? <CheckCircle2 className="w-3.5 h-3.5 text-white" /> : null}
        </button>
      ) : (
        <Icon className="mt-0.5 w-5 h-5 shrink-0" style={{ color: meta.color }} />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm font-medium text-foreground">{check.label}</p>
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${meta.color}15`, color: meta.color }}>
            {check.manual && !ticked ? 'Awaiting attestation' : meta.label}
          </span>
        </div>
        {check.detail && <p className="text-xs text-muted-foreground mt-0.5">{check.detail}</p>}
        {check.suggestion && check.status !== 'pass' && (
          <p className="text-xs mt-1 inline-flex items-start gap-1" style={{ color: meta.color }}>
            <Sparkles className="w-3 h-3 mt-0.5 shrink-0" /> {check.suggestion}
          </p>
        )}
      </div>
    </li>
  );
}


/* ────────────────────────────────────────────────────────────────────────── */
/* Tab 3 — Evidence audit log                                                */
/* ────────────────────────────────────────────────────────────────────────── */
function EvidenceAuditTab({ overallColor }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [parentFilter, setParentFilter] = useState('all');
  const [sinceDays, setSinceDays] = useState(0);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [integrity, setIntegrity] = useState(null);  // overrides data.integrity after a manual verify

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const params = {};
      if (parentFilter !== 'all') params.parent_type = parentFilter;
      if (sinceDays > 0) {
        const d = new Date(Date.now() - sinceDays * 24 * 60 * 60 * 1000);
        params.since = d.toISOString();
      }
      if (includeDeleted) params.include_deleted = true;
      const r = await axios.get(`${API}/uploads/audit`, { params, withCredentials: true });
      setData(r.data);
      setIntegrity(null);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load audit log.');
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [parentFilter, sinceDays, includeDeleted]);

  const verify = async () => {
    setVerifying(true);
    try {
      const r = await axios.get(`${API}/uploads/integrity`, { withCredentials: true });
      setIntegrity(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Verification failed.');
    }
    setVerifying(false);
  };

  const downloadCsv = () => {
    if (!data?.rows?.length) return;
    const rows = [['Filename', 'Parent type', 'Parent ID', 'Uploaded by', 'Uploaded at', 'Size (KB)', 'Content type', 'Deleted at', 'Deleted by']];
    data.rows.forEach(r => rows.push([
      r.filename || '', r.parent_type || '', r.parent_id || '',
      r.uploaded_by_name || '', r.uploaded_at || '',
      Math.round((r.size || 0) / 1024).toString(), r.content_type || '',
      r.deleted_at || '', r.deleted_by_name || '',
    ]));
    const csv = rows.map(r => r.map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `evidence_audit_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click();
    a.remove(); URL.revokeObjectURL(url);
  };

  const downloadIntegrityPdf = (year) => {
    const url = `${API}/uploads/integrity-report.pdf${year ? `?year=${year}` : ''}`;
    window.open(url, '_blank');
  };

  // Use the freshly-verified integrity if user clicked verify; fall back to
  // the integrity field returned with the audit payload.
  const integ = integrity || data?.integrity;
  const intactColor = integ?.intact ? '#16A34A' : '#DC2626';
  const thisYear = new Date().getFullYear();

  if (err) {
    return <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-600" data-testid="dias-cc-evidence-error">{err}</div>;
  }

  return (
    <div data-testid="dias-cc-evidence">
      {/* Tamper-evident hash chain — DIAS-defensible governance log */}
      {integ && (
        <div data-testid="dias-cc-evidence-integrity"
          className="rounded-2xl border p-3 mb-3 flex items-start gap-3 flex-wrap"
          style={{ borderColor: `${intactColor}40`, backgroundColor: `${intactColor}08` }}>
          <div className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: `${intactColor}15` }}>
            {integ.intact
              ? <ShieldCheck className="w-4 h-4" style={{ color: intactColor }} />
              : <ShieldAlert className="w-4 h-4" style={{ color: intactColor }} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: intactColor }}
                data-testid="dias-cc-evidence-integrity-status">
                {integ.intact ? 'Chain intact' : 'Tamper detected'}
              </span>
              <span className="text-[10px] text-muted-foreground" data-testid="dias-cc-evidence-integrity-events">
                {integ.total_events} event{integ.total_events === 1 ? '' : 's'} on the governance chain
              </span>
              {!integ.intact && (
                <span className="text-[10px] font-semibold" style={{ color: intactColor }}>
                  · {integ.issue_count ?? integ.issues?.length ?? 0} issue(s)
                </span>
              )}
            </div>
            <div className="text-[10px] font-mono text-muted-foreground truncate mt-0.5" title={integ.head_hash}
              data-testid="dias-cc-evidence-integrity-head">
              <strong>Head hash:</strong> {integ.head_hash}
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">
              Every upload &amp; every soft-delete writes a SHA-256-chained row. Any silent edit, deletion or backdate of past evidence
              breaks the chain and is detected here. Hand the integrity-report PDF to your DIAS assessor as proof of evidence governance.
            </p>
            {!integ.intact && integrity?.issues?.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-[11px]" data-testid="dias-cc-evidence-integrity-issues">
                {integrity.issues.slice(0, 5).map((i, ix) => (
                  <li key={ix} className="font-mono" style={{ color: intactColor }}>
                    seq #{i.at_seq}: {i.issue}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap shrink-0">
            <button onClick={verify} disabled={verifying} data-testid="dias-cc-evidence-verify-btn"
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border disabled:opacity-50"
              style={{ borderColor: `${intactColor}40`, color: intactColor }}>
              {verifying ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
              {verifying ? 'Verifying…' : 'Verify integrity'}
            </button>
            <button onClick={() => downloadIntegrityPdf(thisYear)} data-testid="dias-cc-evidence-report-pdf-btn"
              title={`Annual evidence-integrity report PDF (${thisYear}) — chain status, monthly counts, top uploaders, every event with hash. Hand to your DIAS assessor.`}
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md text-white"
              style={{ backgroundColor: intactColor }}>
              <FileText className="w-3 h-3" /> Integrity report ({thisYear})
            </button>
            <button onClick={() => downloadIntegrityPdf(0)} data-testid="dias-cc-evidence-report-pdf-all-btn"
              title="All-time evidence integrity report — every event since the chain began."
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border"
              style={{ borderColor: `${intactColor}40`, color: intactColor }}>
              <Download className="w-3 h-3" /> All-time
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="dias-cc-evidence-filters">
        <select value={parentFilter} onChange={e => setParentFilter(e.target.value)}
          data-testid="dias-cc-evidence-parent-filter"
          className="text-xs bg-background border border-border rounded-md px-2 py-1.5">
          <option value="all">All evidence types</option>
          {Object.entries(PARENT_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={sinceDays} onChange={e => setSinceDays(Number(e.target.value))}
          data-testid="dias-cc-evidence-since-filter"
          className="text-xs bg-background border border-border rounded-md px-2 py-1.5">
          <option value={0}>All time</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 12 months</option>
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer"
          data-testid="dias-cc-evidence-include-deleted-label">
          <input type="checkbox" checked={includeDeleted} onChange={e => setIncludeDeleted(e.target.checked)}
            data-testid="dias-cc-evidence-include-deleted"
            className="w-3.5 h-3.5 accent-current" />
          Include deleted (tombstoned)
        </label>
        <div className="ml-auto flex items-center gap-2">
          <button onClick={load} disabled={loading} data-testid="dias-cc-evidence-refresh"
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border disabled:opacity-50"
            style={{ borderColor: `${overallColor}40`, color: overallColor }}>
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
          </button>
          <button onClick={downloadCsv} disabled={!data?.rows?.length}
            data-testid="dias-cc-evidence-csv"
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-md border disabled:opacity-50"
            style={{ borderColor: `${overallColor}40`, color: overallColor }}>
            <Download className="w-3 h-3" /> CSV
          </button>
        </div>
      </div>

      {/* Summary tiles */}
      {data?.by_parent_type && Object.keys(data.by_parent_type).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-4" data-testid="dias-cc-evidence-summary">
          {Object.entries(data.by_parent_type).map(([k, v]) => (
            <div key={k} className="border border-border rounded-lg px-3 py-2 bg-background">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {PARENT_TYPE_LABELS[k] || k}
              </div>
              <div className="text-base font-bold text-foreground tabular-nums">{v.count}</div>
              <div className="text-[10px] text-muted-foreground">{Math.round((v.bytes || 0) / 1024)} KB total</div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      {!data && loading && (
        <div className="animate-pulse h-32 bg-muted/40 rounded-xl" data-testid="dias-cc-evidence-loading" />
      )}
      {data && data.rows.length === 0 && (
        <div className="border border-border rounded-2xl p-6 text-center" data-testid="dias-cc-evidence-empty">
          <FileText className="w-7 h-7 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm font-semibold text-foreground">No evidence uploads match these filters.</p>
          <p className="text-[11px] text-muted-foreground">Try widening the date range or selecting "All evidence types".</p>
        </div>
      )}
      {data && data.rows.length > 0 && (
        <div className="border border-border rounded-2xl overflow-hidden" data-testid="dias-cc-evidence-table">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <Th>Filename</Th>
                  <Th>Type</Th>
                  <Th>Uploaded by</Th>
                  <Th>When</Th>
                  <Th className="text-right">Size</Th>
                  <Th />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.rows.map(r => {
                  const tomb = !!r.deleted_at;
                  return (
                  <tr key={r.id} className={`hover:bg-muted/20 ${tomb ? 'opacity-60' : ''}`} data-testid={`dias-cc-evidence-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-foreground truncate max-w-xs" title={r.filename}>
                        {r.filename}
                        {tomb && <span className="ml-2 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ backgroundColor: '#DC262615', color: '#DC2626' }} data-testid={`dias-cc-evidence-tombstone-${r.id}`}>Deleted</span>}
                      </div>
                      {r.label && <div className="text-[10px] text-muted-foreground truncate max-w-xs">{r.label}</div>}
                      {tomb && <div className="text-[10px] text-red-500/70 truncate max-w-xs">
                        Tombstoned {relTime(r.deleted_at)} by {r.deleted_by_name || '—'}
                      </div>}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-block px-1.5 py-0.5 rounded font-mono text-[10px]"
                        style={{ backgroundColor: `${overallColor}10`, color: overallColor }}>
                        {PARENT_TYPE_LABELS[r.parent_type] || r.parent_type}
                      </span>
                      <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[16ch]" title={r.parent_id}>
                        #{r.parent_id}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{r.uploaded_by_name || '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground tabular-nums">
                      <span title={r.uploaded_at}>{relTime(r.uploaded_at)}</span>
                    </td>
                    <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">
                      {Math.round((r.size || 0) / 1024)} KB
                    </td>
                    <td className="px-3 py-2 text-right">
                      {tomb ? (
                        <span className="text-[11px] text-muted-foreground/60 inline-flex items-center gap-1"
                          title="File was tombstoned — original bytes are no longer on disk but the chain entry remains.">
                          <XCircle className="w-3 h-3" /> Gone
                        </span>
                      ) : (
                        <a href={`${API}/uploads/${r.id}`} target="_blank" rel="noreferrer"
                          data-testid={`dias-cc-evidence-view-${r.id}`}
                          className="text-[11px] font-semibold inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
                          <FileSearch className="w-3 h-3" /> View
                        </a>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-3 py-2 text-[10px] text-muted-foreground bg-muted/20 border-t border-border flex items-center gap-2 flex-wrap">
            <History className="w-3 h-3" />
            Showing {data.total_returned} record{data.total_returned === 1 ? '' : 's'} · capped at 500.
            {data.as_of && <span>As of {new Date(data.as_of).toLocaleString('en-AU')}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children, className = '' }) {
  return <th className={`px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground ${className}`}>{children}</th>;
}

function relTime(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return iso;
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString('en-AU');
}


/* ────────────────────────────────────────────────────────────────────────── */
/* Sub-components reused from the older widget                               */
/* ────────────────────────────────────────────────────────────────────────── */
function BucketRow({ bucket }) {
  const c = color(bucket.score);
  const link = BUCKET_LINKS[bucket.label];
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border rounded-xl p-3 bg-background"
      data-testid={`dias-cc-bucket-${bucket.label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <button onClick={() => setOpen(o => !o)}
              className="text-sm font-semibold text-foreground hover:underline text-left">
              {bucket.label}
            </button>
            <span className="text-xs font-bold tabular-nums" style={{ color: c }}>
              {bucket.score}% · {bucket.passed}/{bucket.total}
            </span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all"
              style={{ width: `${bucket.score}%`, backgroundColor: c }} />
          </div>
        </div>
        {link && (
          <Link to={link.href}
            data-testid={`dias-cc-bucket-link-${bucket.label.replace(/\s+/g, '-').toLowerCase()}`}
            className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md border whitespace-nowrap"
            style={{ borderColor: `${c}40`, color: c }}>
            {link.label} <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>
      {open && (
        <ul className="mt-3 space-y-1.5 pt-2 border-t border-border">
          {bucket.items.map((it, idx) => (
            <li key={idx} className="flex items-center gap-2 text-xs">
              {it.ok
                ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                : <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
              <span className={`flex-1 ${it.ok ? 'text-foreground' : 'text-muted-foreground'}`}>{it.label}</span>
              <span className="text-[10px] text-muted-foreground tabular-nums">{it.detail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ScoreRing({ value, color }) {
  const size = 96, stroke = 8, r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="currentColor" strokeWidth={stroke}
        fill="transparent" className="text-muted/40" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke}
        fill="transparent" strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      <text x="50%" y="50%" dy="0.36em" textAnchor="middle"
        className="text-2xl font-bold fill-foreground rotate-90 origin-center"
        transform={`rotate(90 ${size / 2} ${size / 2})`}>
        {value}
      </text>
    </svg>
  );
}

function ShareLinkModal({ onClose }) {
  const [tokens, setTokens] = useState([]);
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState('QIP visit');
  const [days, setDays] = useState(30);
  const [copied, setCopied] = useState('');
  const load = async () => {
    const r = await axios.get(`${API}/qip-share/tokens`, { withCredentials: true });
    setTokens(r.data);
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    setCreating(true);
    try {
      await axios.post(`${API}/qip-share/tokens`, { label, days_valid: days }, { withCredentials: true });
      load();
    } finally { setCreating(false); }
  };
  const revoke = async (token) => {
    if (!window.confirm('Revoke this share link? It cannot be un-revoked.')) return;
    await axios.delete(`${API}/qip-share/tokens/${token}`, { withCredentials: true });
    load();
  };
  const copy = (token) => {
    const url = `${window.location.origin}/qip-share/${token}`;
    navigator.clipboard.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(''), 2000);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="dias-cc-share-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background border border-border rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5">
        <h3 className="font-['Outfit'] text-lg font-bold mb-1">QIP share links</h3>
        <p className="text-xs text-muted-foreground mb-4">Tokenised read-only URL — no login required. Hand to the QIP assessor before site visit. Revoke any time.</p>
        <div className="grid grid-cols-12 gap-2 mb-4 border-b border-border pb-3">
          <div className="col-span-7">
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Label</label>
            <input value={label} onChange={e => setLabel(e.target.value)}
              data-testid="share-label" placeholder="e.g. QIP pre-visit · Apr 2026"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Valid days</label>
            <input type="number" value={days} onChange={e => setDays(Number(e.target.value) || 30)}
              data-testid="share-days" min="1" max="365"
              className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
          </div>
          <div className="col-span-3 flex items-end">
            <button onClick={create} disabled={creating} data-testid="share-create-btn"
              className="w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg text-white disabled:opacity-50"
              style={{ backgroundColor: '#42CDD7' }}>
              <Share2 className="w-3.5 h-3.5" /> {creating ? 'Creating…' : 'Create link'}
            </button>
          </div>
        </div>
        {tokens.length === 0
          ? <p className="text-xs text-muted-foreground italic py-4 text-center">No share links yet.</p>
          : <div className="space-y-1.5" data-testid="share-list">
            {tokens.map(t => {
              const url = `${window.location.origin}/qip-share/${t.token}`;
              const expired = t.expires_at < new Date().toISOString();
              const isCopied = copied === t.token;
              return (
                <div key={t.token} className={`bg-muted/40 rounded-lg p-3 text-xs ${t.revoked || expired ? 'opacity-50' : ''}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-foreground">
                        {t.label}
                        {t.revoked && <span className="ml-2 text-[9px] font-bold uppercase text-red-500">Revoked</span>}
                        {!t.revoked && expired && <span className="ml-2 text-[9px] font-bold uppercase text-amber-500">Expired</span>}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        Created {new Date(t.created_at).toLocaleDateString('en-AU')} · expires {new Date(t.expires_at).toLocaleDateString('en-AU')} · viewed {t.view_count}×
                      </p>
                      <p className="text-[10px] font-mono text-muted-foreground truncate mt-1">{url}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => copy(t.token)} disabled={t.revoked || expired}
                        className="text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md border disabled:opacity-30"
                        style={{ borderColor: '#42CDD740', color: '#42CDD7' }}>
                        <Copy className="w-3 h-3" /> {isCopied ? 'Copied!' : 'Copy URL'}
                      </button>
                      {!t.revoked && (
                        <button onClick={() => revoke(t.token)}
                          className="p-1.5 text-muted-foreground hover:text-red-500" title="Revoke">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>}
        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-border">Close</button>
        </div>
      </div>
    </div>
  );
}
